import { inject, Injectable, Injector, signal } from '@angular/core';
import { ProjectsFeatureService } from '../projects-feature/projects-feature.service';
import {
  DeleteTimesheetLineRequestParams,
  InsertTimesheetLineRequestParams,
  Timesheet,
  TimesheetLine,
  TimesheetLinesByDate,
  TimesheetLinesByMonth,
  TimesheetLinesByWeek,
  TimesheetsLinesDomain,
} from '../../domain/timesheets-domain';
import { TimesheetsService } from '../../services/timesheets-service/timesheets-service.service';
import { SqlService, TimesheetEntryLinkRecord } from '../../services/sql-service/sql-service.service';
import { finalize, map, of, switchMap, tap } from 'rxjs';
import { EmployeeFeatureService } from '../employee-feature/employee-feature.service';
import { AuthenticationFeatureService } from '../authentication-feature/authentication-feature.service';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekday from 'dayjs/plugin/weekday';
import { MatDialog } from '@angular/material/dialog';
import { FillHoursDialogComponent } from '../../dialog/fill-hours-dialog/fill-hours-dialog.component';
import { DateFormats } from '../../constants/datetime.constants';
import { DeleteEntryDialogComponent } from '../../dialog/delete-entry-dialog/delete-entry-dialog.component';
import { groupTimesheetLinesByMonth, groupTimesheetLinesByWeek, isValidDailyTotalWithEdit } from './utils';
import { groupTimesheetLinesByDate } from './utils/groupTimesheetLinesByDate.util';

dayjs.extend(isBetween);
dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);
dayjs.extend(weekday);

export type GroupByDateTypes = 'day' | 'week' | 'month';

@Injectable({
  providedIn: 'root',
})
export class TimesheetsFeatureService {
  private _dialog = inject(MatDialog);

  isLoadingTimesheetHeaders = signal<boolean>(false);
  timesheetHeaders = signal<Timesheet[]>([]);
  selectedTimesheetHeader = signal<Timesheet | null>(null);

  isLoadingTimesheetLines = signal<boolean>(false);
  timesheetLines = signal<TimesheetLine[]>([]);
  selectedTimesheetLine = signal<TimesheetLine | null>(null);
  copyTimesheetLine = signal<TimesheetLine | null>(null);
  presetProjectFilter = signal<{ projectNo: string; phaseNo: string; taskNo: string } | null>(null);

  isLoadingSubmittingTimesheetHeader = signal<boolean>(false);
  isLoadingSubmittingTimesheetLine = signal<boolean>(false);
  isLoadingUpdatingTimesheetLine = signal<boolean>(false);
  isLoadingDeletingTimesheetLine = signal<boolean>(false);

  totalHoursAccumulated = 0;
  entryLinksMap = signal<Map<string, boolean>>(new Map());

  filterTimesheetLines = signal<{ startDate: Dayjs; endDate: Dayjs; projectNo: string; phaseNo: string; taskNo: string }>({
    startDate: dayjs(),
    endDate: dayjs(),
    projectNo: '',
    phaseNo: '',
    taskNo: '',
  });
  searchText = signal<string>('');
  groupByDateTypes = signal<GroupByDateTypes>('day');

  constructor(
    private _timesheetService: TimesheetsService,
    private _sqlService: SqlService,
    private _authFeature: AuthenticationFeatureService,
    private injector: Injector,
  ) {}

  private getUserEmail(): string {
    return this._authFeature.getInstance().getActiveAccount()?.username ?? '';
  }

  /**
   * @description fetch timesheet headers list from api service
   * */
  fetchTimesheetHeaders() {
    this.isLoadingTimesheetHeaders.update(() => true);
    return this._timesheetService.fetchTimesheetHeaders().pipe(
      tap((timesheetRoot) => {
        this.timesheetHeaders.set(timesheetRoot.value);
      }),
      switchMap((timesheetRoot) => {
        const timesheetOfTheWeek = this.generateTimesheetOfTheWeek();

        if (!timesheetOfTheWeek) {
          return this.insertTimesheetHeader(dayjs());
        }

        this.selectedTimesheetHeader.set(timesheetOfTheWeek);
        return of(timesheetRoot);
      }),
      finalize(() => this.isLoadingTimesheetHeaders.update(() => false)),
    );
  }

  /**
   * @description fetch timesheet lines list or time entries from api service
   * */
  fetchTimesheetLines() {
    this.isLoadingTimesheetLines.update(() => true);
    this._sqlService.fetchTimesheetEntryLinks(this.getUserEmail()).subscribe({
      next: (links) => {
        const map = new Map<string, boolean>();
        links.forEach((l) => map.set(`${l.BCEntryId}_${l.BCTimesheetId}`, l.IsZohoSynced));
        this.entryLinksMap.set(map);
      },
    });
    return this._timesheetService.fetchTimesheetLines().pipe(
      tap((timesheetLineRoot) => {
        this.timesheetLines.update((value) => new TimesheetsLinesDomain(timesheetLineRoot.value).newTimesheetLines);
      }),
      finalize(() => this.isLoadingTimesheetLines.update(() => false)),
    );
  }

  /**
   * @description insert timesheet header for the week if not yet on the list
   */
  insertTimesheetHeader(date: Dayjs) {
    this.isLoadingSubmittingTimesheetHeader.update(() => true);
    const _employeeFeature = this.injector.get(EmployeeFeatureService);
    const employeeNo = _employeeFeature.getEmployeeNo();

    const monday = date.isoWeekday(1).startOf('day');
    const sunday = date.isoWeekday(7).startOf('day');
    const firstOfMonth = date.startOf('month');
    const lastOfMonth = date.endOf('month').startOf('day');
    const startingDate = monday.isBefore(firstOfMonth) ? firstOfMonth : monday;
    const endingDate = sunday.isAfter(lastOfMonth) ? lastOfMonth : sunday;

    if (employeeNo) {
      return this._timesheetService.insertTimesheetHeader({
        employeeNo,
        startingDate: startingDate.format('YYYY-MM-DD'),
        endingDate: endingDate.format('YYYY-MM-DD'),
      }).pipe(
        tap((timesheetOfTheWeek) => {
          this.selectedTimesheetHeader.set(timesheetOfTheWeek as Timesheet);
          this.timesheetHeaders.set([...this.timesheetHeaders(), timesheetOfTheWeek]);
        }),
        finalize(() => {
          this.isLoadingSubmittingTimesheetHeader.update(() => false);
        }),
      );
    }

    return of();
  }

  /**
   * @description delete timesheet line for the week if not yet on the list
   */
  deleteTimesheetLine(params: DeleteTimesheetLineRequestParams) {
    this.isLoadingDeletingTimesheetLine.set(true);
    return this._timesheetService.deleteTimesheetLine(params).pipe(
      tap(() => {
        if (params.entryNo && params.timesheetNo) {
          this._sqlService.deleteTimesheetEntryLink(params.entryNo, params.timesheetNo, this.getUserEmail()).subscribe({
            error: (err) => console.error('[SQL] Failed to delete TimesheetEntryLink:', err),
          });
        }
      }),
      finalize(() => {
        this.isLoadingDeletingTimesheetLine.update(() => false);
        this.fetchTimesheetLines().subscribe();
      }),
    );
  }

  handleOpeningAddingFillHourDialog() {
    this.selectedTimesheetLine.set(null);
    this._dialog.open(FillHoursDialogComponent);
  }

  handleOpeningAddHourDialogForProject(projectNo: string, phaseNo = '', taskNo = '') {
    this.selectedTimesheetLine.set(null);
    this.presetProjectFilter.set({ projectNo, phaseNo, taskNo });
    this._dialog.open(FillHoursDialogComponent);
  }

  handleOpeningEditingTimesheetLineDialog() {
    this._dialog.open(FillHoursDialogComponent);
  }

  handleOpeningCopyingTimesheetLineDialog(line: TimesheetLine) {
    this.selectedTimesheetLine.set(null);
    this.copyTimesheetLine.set(line);
    this._dialog.open(FillHoursDialogComponent);
  }

  handleOpeningDeleteTimesheetLineDialog() {
    this._dialog.open(DeleteEntryDialogComponent);
  }

  // generates timesheet of the week for timesheet headers to allow creating timesheet entries
  generateTimesheetOfTheWeek(selectedDate?: Dayjs | null) {
    const _employeeFeature = this.injector.get(EmployeeFeatureService);
    const employeeNo = _employeeFeature.getEmployeeNo();

    return (
      this.timesheetHeaders()
        .filter((timesheet) => timesheet.employeeNo === employeeNo)
        .find((timesheet) => {
          const date = (selectedDate || dayjs()).startOf('day');
          const startingDate = dayjs(timesheet.startingDate).startOf('day');
          const endingDate = dayjs(timesheet.endingDate).startOf('day');

          return date.isBetween(startingDate, endingDate) || date.isSame(startingDate) || date.isSame(endingDate);
        }) ?? null
    );
  }

  resolveTimesheetNo(date: Dayjs) {
    return this._timesheetService.fetchTimesheetHeaders().pipe(
      tap((timesheetRoot) => this.timesheetHeaders.set(timesheetRoot.value)),
      switchMap(() => {
        const header = this.generateTimesheetOfTheWeek(date);
        if (header) return of(header.timesheetNo);
        return this.insertTimesheetHeader(date).pipe(
          map((newHeader) => {
            if (!newHeader?.timesheetNo) throw new Error('Failed to create timesheet header');
            return newHeader.timesheetNo;
          }),
        );
      }),
    );
  }

  handleFilteringTimesheetLines(timesheetLines: TimesheetLine[]): TimesheetLine[] {
    const _employeeFeature = this.injector.get(EmployeeFeatureService);
    const _projectsFeature = this.injector.get(ProjectsFeatureService);
    const employeeNo = _employeeFeature.getEmployeeNo();
    const search = this.searchText().toLowerCase();

    return timesheetLines
      .sort((a, b) => {
        const dateDiff = dayjs(a.activityDate).diff(dayjs(b.activityDate));
        if (dateDiff !== 0) return dateDiff;
        const projectDiff = a.projectNo.localeCompare(b.projectNo);
        if (projectDiff !== 0) return projectDiff;
        const phaseDiff = a.phaseNo.localeCompare(b.phaseNo);
        if (phaseDiff !== 0) return phaseDiff;
        return a.taskNo.localeCompare(b.taskNo);
      })
      .filter((entry) => entry.employeeNo === employeeNo)
      .filter(
        (entry) =>
          dayjs(entry.activityDate).isBetween(this.filterTimesheetLines().startDate, this.filterTimesheetLines().endDate, 'date') ||
          dayjs(entry.activityDate).isSame(this.filterTimesheetLines().startDate, 'date') ||
          dayjs(entry.activityDate).isSame(this.filterTimesheetLines().endDate, 'date'),
      )
      .filter((entry) => entry.projectNo.includes(this.filterTimesheetLines().projectNo))
      .filter((entry) => entry.phaseNo.includes(this.filterTimesheetLines().phaseNo))
      .filter((entry) => entry.taskNo.includes(this.filterTimesheetLines().taskNo))
      .filter((entry) => {
        if (!search) return true;
        const project = _projectsFeature.projects().find((p) => p.projectNo === entry.projectNo);
        const phase = _projectsFeature.phases().find((p) => p.phaseNo === entry.phaseNo && p.projectNo === entry.projectNo);
        const task = _projectsFeature.tasks().find((t) => t.taskNo === entry.taskNo && t.phaseNo === entry.phaseNo);
        return (
          entry.projectNo.toLowerCase().includes(search) ||
          project?.projectName.toLowerCase().includes(search) ||
          entry.phaseNo.toLowerCase().includes(search) ||
          phase?.phaseName.toLowerCase().includes(search) ||
          entry.taskNo.toLowerCase().includes(search) ||
          task?.taskName.toLowerCase().includes(search) ||
          entry.comment?.toLowerCase().includes(search)
        );
      });
  }

  groupTimesheetLinesByDate(timesheetLines: TimesheetLine[]): TimesheetLinesByDate[] {
    this.totalHoursAccumulated = 0;

    const { newTimesheetLines, totalHoursAccumulated } = groupTimesheetLinesByDate(this.handleFilteringTimesheetLines(timesheetLines));

    this.totalHoursAccumulated = totalHoursAccumulated;
    return newTimesheetLines;
  }

  groupTimesheetLinesByWeek(timesheetLines: TimesheetLine[]) {
    return groupTimesheetLinesByWeek(this.groupTimesheetLinesByDate(timesheetLines));
  }

  groupTimesheetLinesByMonth(timesheetLines: TimesheetLine[]): TimesheetLinesByMonth[] {
    return groupTimesheetLinesByMonth(this.groupTimesheetLinesByDate(timesheetLines));
  }

  submitTimesheetEntry(data: InsertTimesheetLineRequestParams) {
    this.isLoadingSubmittingTimesheetLine.update(() => true);
    return this._timesheetService.insertTimesheetLine(data).pipe(
      tap((response) => {
        if (response.entryNo) {
          this._sqlService.createTimesheetEntryLink(response.entryNo, response.timesheetNo, this.getUserEmail()).subscribe({
            error: (err) => console.error('[SQL] Failed to create TimesheetEntryLink:', err),
          });
        }
      }),
      finalize(() => {
        this.isLoadingSubmittingTimesheetLine.update(() => false);
        this.fetchTimesheetLines().subscribe();
      }),
    );
  }

  updateTimesheetEntry(data: InsertTimesheetLineRequestParams) {
    this.isLoadingUpdatingTimesheetLine.update(() => true);
    return this._timesheetService.updateTimesheetLine(data).pipe(
      finalize(() => {
        this.isLoadingUpdatingTimesheetLine.update(() => false);
        this.fetchTimesheetLines().subscribe();
      }),
    );
  }

  resetTimesheetLinesFilter() {
    this.filterTimesheetLines.set({ startDate: dayjs(), endDate: dayjs(), projectNo: '', phaseNo: '', taskNo: '' });
    this.searchText.set('');
    this.groupByDateTypes.set('day');
  }

  // validate new entry 24-hour limitation
  validateNewEntryHoursLog(entryDate: Dayjs, duration: number): boolean {
    const _employeeFeature = this.injector.get(EmployeeFeatureService);
    const employeeNo = _employeeFeature.getEmployeeNo();
    const formattedEntryDate = dayjs(entryDate).format(DateFormats.DATE_YEAR_MONTH_DAY_KABOB);

    const totalHoursOfDate = this.timesheetLines()
      .filter((timesheetLine) => timesheetLine.activityDate === formattedEntryDate && timesheetLine.employeeNo === employeeNo)
      .reduce((accumulator, currentValue) => accumulator + currentValue.totalTime, 0);

    return totalHoursOfDate + duration <= 24;
  }

  // validate old entry 24-hour limitation
  validateOldEntryHoursLog(entryDate: Dayjs, duration: number, entryNo?: number): boolean {
    if (!entryNo) return false;

    const employeeNo = this.injector.get(EmployeeFeatureService).getEmployeeNo();
    return isValidDailyTotalWithEdit(this.timesheetLines(), entryDate, employeeNo, entryNo, duration);
  }
}
